import React, { useEffect, useMemo, useState, startTransition } from 'react';
import { useLocale } from '../contexts/LocaleContext';
import { useTranslation } from 'react-i18next';
import { useTenant } from '../contexts/TenantContext';
import { Link, useNavigate } from 'react-router-dom';
import { useMenuTranslations } from '../hooks/useMenuTranslations';

const defaultFlags = [
    { code: "it", img: "/flags/it.png" },
    { code: "en", img: "/flags/en.png" },
    { code: "de", img: "/flags/de.png" },
    { code: "fr", img: "/flags/fr.svg" }
];

const Home: React.FC = () => {
    const localeContext = useLocale();
    const { t } = useTranslation();
    const { locations, currentLocation, switchLocation, tenantInfo, tenantId } = useTenant();
    const navigate = useNavigate();
    const { setLanguage, availableLanguages, isLoading: translationsLoading } = useMenuTranslations();

    const lingua = localeContext?.lingua ?? '';
    const setLingua = localeContext?.setLingua ?? (() => { });

    const [backgroundLoaded, setBackgroundLoaded] = useState(false);
    const [isLanguageTransitioning, setIsLanguageTransitioning] = useState(false);
    const [ripples, setRipples] = useState<{ id: number; btn: string; x: number; y: number }[]>([]);

    const backgroundImageUrl = useMemo(() => {
        if (tenantInfo && (tenantInfo as any).backgroundImageUrl) {
            return String((tenantInfo as any).backgroundImageUrl);
        }
        return null;
    }, [tenantInfo]);

    const languagesToRender = useMemo(() => {
        const availableSet = new Set(availableLanguages && availableLanguages.length ? availableLanguages : defaultFlags.map(l => l.code));
        const known = defaultFlags.filter(flag => availableSet.has(flag.code));
        const extra = Array.from(availableSet)
            .filter(code => !known.some(flag => flag.code === code))
            .map(code => ({ code, img: `/flags/${code}.png` }));
        return [...known, ...extra];
    }, [availableLanguages]);

    const fallbackGradient = useMemo(() => {
        if (tenantInfo?.primaryColor) {
            return `linear-gradient(135deg, ${tenantInfo.primaryColor} 0%, rgba(255,255,255,0.88) 55%, rgba(255,255,255,0.95) 100%)`;
        }
        return 'linear-gradient(135deg, #f8f5f2 0%, #fdfdfd 55%, #ffffff 100%)';
    }, [tenantInfo?.primaryColor]);

    useEffect(() => {
        if (!backgroundImageUrl) {
            setBackgroundLoaded(false);
            return;
        }

        let cancelled = false;
        const img = new Image();
        img.decoding = 'async';
        img.src = backgroundImageUrl;
        img.onload = () => {
            if (!cancelled) {
                setBackgroundLoaded(true);
            }
        };
        img.onerror = () => {
            if (!cancelled) {
                setBackgroundLoaded(false);
            }
        };

        return () => {
            cancelled = true;
        };
    }, [backgroundImageUrl]);

    // Etichette del bottone di accesso menu: mappa LOCALE (non tocca i file di traduzione condivisi dei tenant)
    const ctaTexts: Record<string, { enter: string; hint: string }> = {
        it: { enter: 'Entra nel menu', hint: 'Tocca per vedere il menu' },
        en: { enter: 'Enter the menu', hint: 'Tap to view the menu' },
        de: { enter: 'Zum Menü', hint: 'Tippen, um das Menü zu sehen' },
        fr: { enter: 'Accéder au menu', hint: 'Touchez pour voir le menu' },
    };
    const ctaText = ctaTexts[lingua] || ctaTexts.it;

    // Sceglie testo bianco o scuro in base alla luminosità del colore di sfondo (contrasto leggibile per ogni tenant)
    const getContrastText = (hex: string) => {
        const h = (hex || '#e91e63').replace('#', '');
        const full = h.length === 3 ? h.split('').map(c => c + c).join('') : h;
        const r = parseInt(full.substring(0, 2), 16);
        const g = parseInt(full.substring(2, 4), 16);
        const b = parseInt(full.substring(4, 6), 16);
        if ([r, g, b].some(v => Number.isNaN(v))) return '#ffffff';
        // Luminanza percepita (YIQ): >= 150 => sfondo chiaro => testo scuro
        const yiq = (r * 299 + g * 587 + b * 114) / 1000;
        return yiq >= 150 ? '#1f2937' : '#ffffff';
    };

    const hexToRgba = (hex: string, alpha: number) => {
        const h = (hex || '#e91e63').replace('#', '');
        const full = h.length === 3 ? h.split('').map(c => c + c).join('') : h;
        const r = parseInt(full.substring(0, 2), 16) || 233;
        const g = parseInt(full.substring(2, 4), 16) || 30;
        const b = parseInt(full.substring(4, 6), 16) || 99;
        return `rgba(${r}, ${g}, ${b}, ${alpha})`;
    };

    const spawnRipple = (btn: string, e: React.MouseEvent<HTMLButtonElement>) => {
        const rect = e.currentTarget.getBoundingClientRect();
        const id = Date.now();
        setRipples(prev => [...prev, { id, btn, x: e.clientX - rect.left, y: e.clientY - rect.top }]);
        setTimeout(() => setRipples(prev => prev.filter(r => r.id !== id)), 650);
    };

    const handleMainCtaClick = (e: React.MouseEvent<HTMLButtonElement>) => {
        spawnRipple('main', e);
        navigate(tenantId ? `/menu?tenantId=${tenantId}` : '/menu');
    };

    const handleLocationClick = (locId: string, e: React.MouseEvent<HTMLButtonElement>) => {
        spawnRipple(`loc-${locId}`, e);
        switchLocation(locId);
        navigate(tenantId ? `/menu?tenantId=${tenantId}` : '/menu');
    };

    // Funzione per cambiare lingua sincronizzando locale e traduzioni menu
    const handleLanguageChange = (langCode: string) => {
        if (langCode === lingua) return;

        setIsLanguageTransitioning(true);
        startTransition(() => {
            setLingua(langCode);
            setLanguage(langCode);
        });
    };

    useEffect(() => {
        if (!isLanguageTransitioning) return;
        if (translationsLoading) return;

        const timeout = window.setTimeout(() => setIsLanguageTransitioning(false), 180);
        return () => window.clearTimeout(timeout);
    }, [isLanguageTransitioning, translationsLoading]);

    const showLanguageSpinner = translationsLoading || isLanguageTransitioning;

    // Import UI rimosso dalla home pubblica (sarà disponibile in un pannello admin dedicato)

    return (
        <div className="home-root">
            {/* Background trasparente/foto */}
            <div
                className={`home-bg${backgroundLoaded ? ' is-loaded' : ''}`}
                style={{
                    background: fallbackGradient,
                }}
                data-testid="home-background"
            >
                <div
                    className="home-bg__image"
                    style={{
                        backgroundImage: backgroundImageUrl ? `url('${backgroundImageUrl}')` : undefined,
                    }}
                />
            </div>
            {/* Contenuto Home sopra il background */}
            <div className="home-content" style={{ position: 'relative', zIndex: 1 }}>
                <div className={`home-card${showLanguageSpinner ? ' home-card--loading' : ''}`}>
                    {showLanguageSpinner && (
                        <div className="home-card__loading" role="status" aria-live="polite">
                            <span className="home-card__spinner" />
                            <span className="home-card__loading-text">{t('home.loadingLanguage', 'Aggiorno la lingua…')}</span>
                        </div>
                    )}
                    {tenantInfo?.logoUrl && (
                        <img
                            src={tenantInfo.logoUrl}
                            alt={tenantInfo?.nome || 'Logo'}
                            className="home-logo home-logo-animate"
                            onError={(e) => { e.currentTarget.style.display = 'none'; }}
                        />
                    )}
                    <h2
                        className="home-title home-title-animate"
                        style={{
                            textAlign: 'center',
                            fontWeight: 600,
                            fontSize: 'clamp(1.15rem, 3.6vw, 1.75rem)',
                            lineHeight: 1.15,
                            margin: '0.9rem 0 0.55rem',
                            whiteSpace: 'normal',
                            wordBreak: 'break-word',
                            overflowWrap: 'anywhere',
                            maxWidth: '90%',
                            marginLeft: 'auto',
                            marginRight: 'auto'
                        }}
                    >
                        <span style={{ display: 'inline-block', verticalAlign: 'baseline' }}>{t('home.welcome')}</span>{' '}
                        {tenantInfo?.nome ? <span style={{ fontWeight: 700 }}>{tenantInfo.nome}</span> : null}
                    </h2>
                    <p className="home-subtitle" style={{ textAlign: 'center', marginTop: 8 }}>
                        {t('home.slogan')}
                    </p>
                    <div className="home-buttons">
                        {locations && locations.length > 0 ? (
                            locations.map(loc => (
                                <button
                                    key={loc.id}
                                    className={`home-main-btn${currentLocation === loc.id ? ' selected' : ''}`}
                                    onClick={(e) => handleLocationClick(loc.id, e)}
                                    aria-label={ctaText.hint}
                                    style={{
                                        marginTop: '12px',
                                        padding: '16px 20px 18px',
                                        borderRadius: 20,
                                        border: 'none',
                                        background: (tenantInfo && tenantInfo.primaryColor) ? tenantInfo.primaryColor : '#e91e63',
                                        color: getContrastText((tenantInfo && tenantInfo.primaryColor) || '#e91e63'),
                                        // @ts-ignore custom property per il colore del glow
                                        '--cta-ring-color': hexToRgba((tenantInfo && tenantInfo.primaryColor) || '#e91e63', 0.45),
                                        cursor: 'pointer'
                                    } as React.CSSProperties}
                                >
                                    <span className="home-main-btn__label">{loc.nome}</span>
                                    <span className="home-main-btn__hint">{ctaText.hint} <span aria-hidden="true">→</span></span>
                                    {ripples.filter(r => r.btn === `loc-${loc.id}`).map(r => (
                                        <span key={r.id} className="home-main-btn__ripple" style={{ left: r.x, top: r.y }} />
                                    ))}
                                </button>
                            ))
                        ) : (
                            <button
                                className="home-main-btn"
                                onClick={handleMainCtaClick}
                                aria-label={ctaText.hint}
                                style={{
                                    marginTop: '12px',
                                    padding: '16px 20px 18px',
                                    borderRadius: 20,
                                    border: 'none',
                                    background: (tenantInfo && tenantInfo.primaryColor) ? tenantInfo.primaryColor : '#e91e63',
                                    color: getContrastText((tenantInfo && tenantInfo.primaryColor) || '#e91e63'),
                                    // @ts-ignore custom property per il colore del glow
                                    '--cta-ring-color': hexToRgba((tenantInfo && tenantInfo.primaryColor) || '#e91e63', 0.45),
                                    cursor: 'pointer'
                                } as React.CSSProperties}
                            >
                                <span className="home-main-btn__label">{tenantInfo?.nome || ctaText.enter}</span>
                                <span className="home-main-btn__hint">{ctaText.hint} <span aria-hidden="true">→</span></span>
                                {ripples.filter(r => r.btn === 'main').map(r => (
                                    <span key={r.id} className="home-main-btn__ripple" style={{ left: r.x, top: r.y }} />
                                ))}
                            </button>
                        )}
                    </div>
                    <div className="home-languages">
                        {languagesToRender.map(lang => (
                            <img
                                key={lang.code}
                                src={lang.img}
                                alt={lang.code}
                                className={`language-flag${lingua === lang.code ? ' selected' : ''}${showLanguageSpinner && lingua === lang.code ? ' loading' : ''}`}
                                style={{ cursor: 'pointer' }}
                                onClick={() => handleLanguageChange(lang.code)}
                                width={24}
                            />
                        ))}
                    </div>
                    {/* Upload import rimosso */}
                    {/* Pulsante per forzare il ricaricamento dei dati */}
                    {/* Ricarica manuale rimossa (debug) */}
                    <Link
                        to="/allergeni"
                        className="modern-btn"
                        style={{
                            position: 'relative',
                            zIndex: 10,
                            pointerEvents: 'auto',
                            display: 'block',
                            margin: '32px auto 8px auto',
                            fontWeight: 'bold',
                            color: '#e91e63',
                            textAlign: 'center',
                            textDecoration: 'underline'
                        }}
                    >
                        {t('home.allergensInfo')}
                    </Link>
                    <div style={{ textAlign: 'center', fontSize: '0.9em', color: '#888', marginBottom: '8px' }}>
                        Powered by @Qvadis
                    </div>

                    {/* Debug info (visibile solo in development) */}
                    {/* Debug info rimossa in produzione */}
                </div>
            </div>
        </div>
    );
};

export default Home;